import { Helmet } from "@dr.pogodin/react-helmet";
import { Link } from "react-router-dom";
import RecentPosts from "../components/RecentPosts";
import ExpenseTrackerBanner from "../components/ExpenseTrackerBanner";

export default function QuarterlyTaxDeadlines() {
  const deadlines2025 = [
    {
      quarter: "Q1 2025",
      period: "January 1 – March 31, 2025",
      dueDate: "April 15, 2025",
      status: "upcoming",
      note: "Also the deadline for 2024 tax returns"
    },
    {
      quarter: "Q2 2025",
      period: "April 1 – May 31, 2025",
      dueDate: "June 16, 2025",
      status: "upcoming",
      note: "June 15 falls on Sunday, so deadline is June 16"
    },
    {
      quarter: "Q3 2025",
      period: "June 1 – August 31, 2025",
      dueDate: "September 15, 2025",
      status: "upcoming",
      note: ""
    },
    {
      quarter: "Q4 2025",
      period: "September 1 – December 31, 2025",
      dueDate: "January 15, 2026",
      status: "upcoming",
      note: "Or file tax return by January 31, 2026 with full payment"
    }
  ];

  const deadlines2024 = [
    {
      quarter: "Q1 2024",
      period: "January 1 – March 31, 2024",
      dueDate: "April 15, 2024",
      status: "passed"
    },
    {
      quarter: "Q2 2024",
      period: "April 1 – May 31, 2024",
      dueDate: "June 17, 2024",
      status: "passed"
    },
    {
      quarter: "Q3 2024",
      period: "June 1 – August 31, 2024",
      dueDate: "September 16, 2024",
      status: "passed"
    },
    {
      quarter: "Q4 2024",
      period: "September 1 – December 31, 2024",
      dueDate: "January 15, 2025",
      status: "passed"
    }
  ];

  return (
    <>
      <Helmet>
        <title>2025 Quarterly Tax Payment Deadlines | 1099 Estimated Tax Due Dates</title>
        <meta
          name="description"
          content="Never miss a quarterly tax deadline. Complete 2025 estimated tax payment dates for self-employed, freelancers, and 1099 contractors with IRS Form 1040-ES schedule."
        />
        <meta name="keywords" content="quarterly taxes 2025, estimated tax deadlines, 1099 tax due dates, self employment tax deadlines, form 1040-ES deadlines" />
        <link rel="canonical" href="https://free1099calc.com/tools/quarterly-tax-deadlines" />
      </Helmet>

      {/* Hero Section */}
      <div className="relative bg-gradient-to-br from-primary-900 via-primary-800 to-slate-900 overflow-hidden">
        <div 
          className="absolute inset-0 opacity-15"
          style={{
            backgroundImage: 'url("https://images.unsplash.com/photo-1506784983877-45594efa4cbe?w=1920&q=80")',
            backgroundSize: 'cover',
            backgroundPosition: 'center'
          }}
        />
        <div className="absolute inset-0 bg-gradient-to-br from-primary-900/85 to-slate-900/90" />
        
        <div className="relative max-w-6xl mx-auto px-4 py-20 md:py-28">
          <div className="text-center max-w-3xl mx-auto">
            <div className="inline-block px-5 py-2.5 bg-white/15 backdrop-blur-sm border border-white/30 rounded-full mb-6">
              <span className="text-white text-sm font-bold">📅 TAX DEADLINES</span>
            </div>
            <h1 className="text-5xl md:text-6xl font-black text-white mb-6">
              2025 Quarterly Tax Deadlines
            </h1>
            <p className="text-xl md:text-2xl text-slate-300 mb-8">
              Never miss a payment. Track critical estimated tax dates for 1099 contractors.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link
                to="/"
                className="inline-block bg-primary-600 hover:bg-primary-700 text-white px-8 py-4 rounded-lg font-semibold transition-colors shadow-lg"
              >
                Calculate Quarterly Taxes →
              </Link>
              <Link
                to="/tools"
                className="inline-block bg-white/10 hover:bg-white/20 backdrop-blur-sm text-white border border-white/30 px-8 py-4 rounded-lg font-semibold transition-colors"
              >
                More Tools
              </Link>
            </div>
          </div>
        </div>
      </div>

    <div className="min-h-screen bg-slate-50">
      <div className="max-w-4xl mx-auto px-4 py-12">

        {/* Expense Tracker Banner */}
        <div className="mb-8">
          <ExpenseTrackerBanner compact />
        </div>

        {/* Alert box */}
        <div className="bg-gradient-to-r from-yellow-50 to-amber-50 border-2 border-yellow-300 rounded-3xl p-6 mb-8 shadow-lg">
          <div className="flex items-start gap-4">
            <div className="flex-shrink-0">
              <svg className="h-5 w-5 text-yellow-400" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
              </svg>
            </div>
            <div className="ml-3">
              <h3 className="text-sm font-medium text-yellow-800">
                Missing deadlines = penalties & interest
              </h3>
              <p className="mt-2 text-sm text-yellow-700">
                The IRS charges penalties for late or underpaid estimated taxes. Use our{" "}
                <Link to="/" className="underline font-semibold">1099 Tax Calculator</Link> to estimate how much you owe each quarter.
              </p>
            </div>
          </div>
        </div>

        {/* 2025 Deadlines */}
        <section className="mb-12">
          <h2 className="text-3xl font-black text-gray-900 mb-8 flex items-center gap-3">
            <span className="text-primary-600">📅</span>
            2025 Estimated Tax Payment Schedule
          </h2>
          <div className="space-y-6">
            {deadlines2025.map((deadline) => (
              <div key={deadline.quarter} className="bg-white rounded-3xl border-2 border-primary-200 shadow-xl p-8 hover:shadow-2xl transition-shadow">
                <div className="flex flex-col md:flex-row md:items-center md:justify-between">
                  <div className="mb-4 md:mb-0">
                    <h3 className="text-2xl font-black text-gray-900 mb-2">{deadline.quarter}</h3>
                    <p className="text-base text-gray-600 mb-2">Income period: {deadline.period}</p>
                    <p className="text-sm text-gray-500">{deadline.note}</p>
                  </div>
                  <div className="text-right">
                    <div className="inline-block bg-gradient-to-br from-primary-600 to-primary-700 text-white px-6 py-4 rounded-2xl shadow-lg">
                      <div className="text-xs font-bold uppercase tracking-wide mb-1">Due Date</div>
                      <div className="text-2xl font-black">{deadline.dueDate}</div>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </section>

        {/* How much to pay */}
        <section className="bg-gradient-to-br from-blue-50 to-indigo-50 rounded-3xl border-2 border-blue-200 shadow-xl p-10 mb-12">
          <h2 className="text-3xl font-black text-gray-900 mb-6">How Much Should You Pay?</h2>
          <p className="text-gray-700 text-lg mb-4">
            The IRS requires you to pay estimated taxes if you expect to owe <strong>$1,000 or more</strong> when you file your return.
            To avoid penalties, you must pay the lesser of:
          </p>
          <ul className="list-disc pl-6 mb-6 space-y-2 text-slate-700">
            <li><strong>90% of your current year tax liability</strong>, OR</li>
            <li><strong>100% of last year's total tax</strong> (110% if AGI &gt; $150,000)</li>
          </ul>
          
          <div className="bg-blue-50 border-l-4 border-primary-600 p-4 mb-6">
            <h4 className="font-semibold text-slate-900 mb-2">Quick Calculation Method</h4>
            <ol className="list-decimal pl-6 space-y-1 text-sm text-slate-700">
              <li>Use our <Link to="/" className="text-primary-600 underline font-semibold">Tax Calculator</Link> to estimate your annual tax liability</li>
              <li>Divide that number by 4 to get your quarterly payment</li>
              <li>Pay that amount by each deadline above</li>
            </ol>
          </div>

          <p className="text-sm text-slate-600 italic">
            If your income varies throughout the year, you can use the annualized income method to pay different amounts
            each quarter based on when you actually earned the income.
          </p>
        </section>

        {/* How to pay */}
        <section className="bg-white rounded-lg border shadow-sm p-8 mb-12">
          <h2 className="text-2xl font-bold text-slate-900 mb-4">How to Pay Quarterly Taxes</h2>
          
          <div className="space-y-6">
            <div>
              <h3 className="text-lg font-semibold text-slate-900 mb-2">1. IRS Direct Pay (Free & Fast)</h3>
              <p className="text-slate-700 mb-2">
                Pay directly from your bank account at{" "}
                <a href="https://www.irs.gov/payments" target="_blank" rel="noopener noreferrer" className="text-primary-600 underline">
                  irs.gov/payments
                </a>
              </p>
              <p className="text-sm text-slate-600">✓ No fees • ✓ Instant confirmation • ✓ Schedule in advance</p>
            </div>

            <div>
              <h3 className="text-lg font-semibold text-slate-900 mb-2">2. EFTPS (Electronic Federal Tax Payment System)</h3>
              <p className="text-slate-700 mb-2">
                Free enrollment at{" "}
                <a href="https://www.eftps.gov" target="_blank" rel="noopener noreferrer" className="text-primary-600 underline">
                  eftps.gov
                </a>
              </p>
              <p className="text-sm text-slate-600">✓ Schedule payments up to 365 days in advance • ✓ Payment history tracking</p>
            </div>

            <div>
              <h3 className="text-lg font-semibold text-slate-900 mb-2">3. Mail Paper Voucher (Form 1040-ES)</h3>
              <p className="text-slate-700 mb-2">
                Download Form 1040-ES from{" "}
                <a href="https://www.irs.gov/forms-pubs/about-form-1040-es" target="_blank" rel="noopener noreferrer" className="text-primary-600 underline">
                  irs.gov
                </a>
                {" "}and mail with a check
              </p>
              <p className="text-sm text-slate-600">⚠ Allow 7-10 days for mail delivery • Use certified mail for proof</p>
            </div>

            <div>
              <h3 className="text-lg font-semibold text-slate-900 mb-2">4. Credit/Debit Card (Fees Apply)</h3>
              <p className="text-slate-700 mb-2">
                Pay through IRS-approved payment processors
              </p>
              <p className="text-sm text-slate-600">⚠ Fees typically 1.87% - 1.98% of payment amount</p>
            </div>
          </div>
        </section>

        {/* FAQ */}
        <section className="mb-12">
          <h2 className="text-2xl font-bold text-slate-900 mb-6">Frequently Asked Questions</h2>
          
          <div className="space-y-4">
            <div className="bg-white rounded-lg border shadow-sm p-6">
              <h3 className="font-semibold text-slate-900 mb-2">What if I miss a deadline?</h3>
              <p className="text-slate-700 text-sm">
                Pay as soon as possible. The IRS calculates penalties daily from the due date until you pay. The penalty
                is typically 0.5% of the unpaid amount per month, plus interest (currently around 8% annually).
              </p>
            </div>

            <div className="bg-white rounded-lg border shadow-sm p-6">
              <h3 className="font-semibold text-slate-900 mb-2">Do I have to pay all four quarters?</h3>
              <p className="text-slate-700 text-sm">
                You only need to pay for quarters when you have income. If you start freelancing in June, you'd start with
                Q2. However, it's often safer to make all four payments to avoid underpayment penalties.
              </p>
            </div>

            <div className="bg-white rounded-lg border shadow-sm p-6">
              <h3 className="font-semibold text-slate-900 mb-2">Can I adjust my quarterly payments?</h3>
              <p className="text-slate-700 text-sm">
                Yes! If your income increases or decreases during the year, recalculate your tax liability and adjust future
                payments. Many freelancers pay more in Q3 and Q4 to catch up if they underestimated earlier in the year.
              </p>
            </div>

            <div className="bg-white rounded-lg border shadow-sm p-6">
              <h3 className="font-semibold text-slate-900 mb-2">What about state quarterly taxes?</h3>
              <p className="text-slate-700 text-sm">
                Most states follow the same federal quarterly schedule (April 15, June 15, Sept 15, Jan 15). However, a few states
                have different deadlines. Check your state's Department of Revenue website for specific requirements.
              </p>
            </div>

            <div className="bg-white rounded-lg border shadow-sm p-6">
              <h3 className="font-semibold text-slate-900 mb-2">Do I need to file Form 1040-ES?</h3>
              <p className="text-slate-700 text-sm">
                You don't need to <em>file</em> it, but you should use it to calculate your estimated tax. If you pay electronically,
                you don't need to mail the vouchers. The worksheet is helpful for planning and record-keeping.
              </p>
            </div>
          </div>
        </section>

        {/* 2024 reference */}
        <section className="mb-12">
          <h2 className="text-2xl font-bold text-slate-900 mb-4">2024 Deadlines (Reference)</h2>
          <div className="bg-slate-100 rounded-lg p-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {deadlines2024.map((deadline) => (
                <div key={deadline.quarter} className="bg-white rounded p-4">
                  <div className="text-sm font-semibold text-slate-700">{deadline.quarter}</div>
                  <div className="text-xs text-slate-500 mb-2">{deadline.period}</div>
                  <div className="text-sm text-slate-600">Due: {deadline.dueDate}</div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* CTA */}
        <div className="bg-gradient-to-r from-primary-600 to-primary-700 rounded-lg p-8 text-white text-center">
          <h2 className="text-2xl font-bold mb-3">Calculate Your Quarterly Tax Payment</h2>
          <p className="mb-6 text-primary-50">
            Use our free 1099 calculator to estimate your tax liability and determine how much to pay each quarter
          </p>
          <Link
            to="/"
            className="inline-block bg-white text-primary-700 px-8 py-3 rounded-lg font-semibold hover:bg-primary-50 transition-colors"
          >
            Calculate My Taxes →
          </Link>
        </div>

        {/* Recent Posts */}
        <div className="mt-12">
          <RecentPosts />
        </div>
      </div>
    </div>
    </>
  );
}
